import * as THREE from 'three';

export class Minimap {
  public canvas: HTMLCanvasElement;
  public ctx: CanvasRenderingContext2D;
  private container: HTMLElement;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'minimap-container';
    this.container.style.cssText = `
      position: absolute;
      top: 16px;
      right: 16px;
      width: 130px;
      height: 130px;
      border-radius: 50%;
      background: rgba(15, 23, 42, 0.85);
      border: 3px solid #38bdf8;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.6);
      overflow: hidden;
      z-index: 15;
      pointer-events: none;
      transition: transform 0.2s ease, opacity 0.2s ease;
    `;

    // Responsive CSS for mobile screens
    if (!document.getElementById('minimap-mobile-css')) {
      const style = document.createElement('style');
      style.id = 'minimap-mobile-css';
      style.textContent = `
        @media (max-width: 900px), (max-height: 550px) {
          #minimap-container {
            top: 48px !important;
            right: 8px !important;
            width: 80px !important;
            height: 80px !important;
            border-width: 2px !important;
            box-shadow: 0 4px 14px rgba(0, 0, 0, 0.6) !important;
            pointer-events: auto !important;
            cursor: pointer !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    this.canvas = document.createElement('canvas');
    this.canvas.width = 130;
    this.canvas.height = 130;
    this.canvas.style.cssText = `width: 100%; height: 100%;`;
    this.ctx = this.canvas.getContext('2d')!;

    this.container.appendChild(this.canvas);

    // Tap to minimize/restore on mobile
    this.container.addEventListener('click', () => {
      const isMinimized = this.container.getAttribute('data-minimized') === 'true';
      if (isMinimized) {
        this.container.setAttribute('data-minimized', 'false');
        this.container.style.opacity = '1';
        this.container.style.transform = 'scale(1)';
      } else {
        this.container.setAttribute('data-minimized', 'true');
        this.container.style.opacity = '0.35';
        this.container.style.transform = 'scale(0.85)';
      }
    });
  }

  public render(playerPos: THREE.Vector3, playerQuat: THREE.Quaternion, trafficVehicles: Array<{ position: THREE.Vector3 }>) {
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const center = w / 2;
    const scale = 0.9; // meters to pixels

    ctx.clearRect(0, 0, w, h);

    // Background radar circle
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, w, h);

    // Save for rotation around player heading
    const euler = new THREE.Euler().setFromQuaternion(playerQuat, 'YXZ');
    const heading = euler.y;

    ctx.save();
    ctx.translate(center, center);
    // In 3D world space (+Y up), forward (+Z at heading=0) is Ahead (-Y_canvas) and right (-X at heading=0) is Right (+X_canvas)
    ctx.rotate(heading);
    ctx.scale(-1, -1);

    // 1. Draw Grid Roads relative to player
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 7 * scale;

    // Main Avenue Z
    const offX = -playerPos.x * scale;
    const offZ = -playerPos.z * scale;

    ctx.beginPath();
    ctx.moveTo(offX, -150 * scale + offZ);
    ctx.lineTo(offX, 150 * scale + offZ);
    ctx.moveTo(-150 * scale + offX, offZ);
    ctx.lineTo(150 * scale + offX, offZ);
    ctx.stroke();

    // Kreisverkehr Center Circle
    ctx.beginPath();
    ctx.arc(offX, offZ, 14.5 * scale, 0, Math.PI * 2);
    ctx.stroke();

    // 1B. Fahrradstraße (salmon color at z = 30)
    ctx.strokeStyle = '#b07060';
    ctx.lineWidth = 4 * scale;
    ctx.beginPath();
    ctx.moveTo(-116 * scale + offX, 30 * scale + offZ);
    ctx.lineTo(116 * scale + offX, 30 * scale + offZ);
    ctx.stroke();

    // 1C. Landstraße (connecting town ring ±120/200m to Autobahn ±580m)
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 6 * scale;
    ctx.beginPath();
    // North Landstraße: (0, 120) -> (0, 580)
    ctx.moveTo(offX, 120 * scale + offZ);
    ctx.lineTo(offX, 580 * scale + offZ);
    // South Landstraße: (0, -120) -> (0, -580)
    ctx.moveTo(offX, -120 * scale + offZ);
    ctx.lineTo(offX, -580 * scale + offZ);
    // East Landstraße: (120, 0) -> (580, 0)
    ctx.moveTo(120 * scale + offX, offZ);
    ctx.lineTo(580 * scale + offX, offZ);
    // West Landstraße: (-120, 0) -> (-580, 0)
    ctx.moveTo(-120 * scale + offX, offZ);
    ctx.lineTo(-580 * scale + offX, offZ);
    ctx.stroke();

    // 1D. Autobahn Loop (4-sided rectangle at ±580m in blue)
    ctx.strokeStyle = '#2563eb';
    ctx.lineWidth = 5 * scale;
    ctx.beginPath();
    ctx.rect(
      -580 * scale + offX,
      -580 * scale + offZ,
      1160 * scale,
      1160 * scale
    );
    ctx.stroke();

    // 1E. Industrial Zone (x: 380..520, z: -200..200)
    ctx.fillStyle = 'rgba(75, 85, 99, 0.4)';
    ctx.fillRect(
      380 * scale + offX,
      -200 * scale + offZ,
      140 * scale,
      400 * scale
    );

    // 2. Draw Traffic AI Vehicles (Green dots)
    ctx.fillStyle = '#22c55e';
    for (const v of trafficVehicles) {
      const vx = (v.position.x - playerPos.x) * scale;
      const vz = (v.position.z - playerPos.z) * scale;
      ctx.beginPath();
      ctx.arc(vx, vz, 3.5, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();

    // 3. Draw Player Car Icon (Blue arrow pointing UP at center)
    ctx.save();
    ctx.translate(center, center);

    ctx.fillStyle = '#38bdf8';
    ctx.beginPath();
    ctx.moveTo(0, -9);
    ctx.lineTo(6, 7);
    ctx.lineTo(0, 4);
    ctx.lineTo(-6, 7);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.restore();
  }

  public mount(root: HTMLElement) {
    root.appendChild(this.container);
  }
}
